/**
 * Box 8 (spec §5.7), the prototype's allocationBox, through D1's OverviewCard and OverviewTable. Per channel of the
 * simulator's budget, in its order:
 * - a head row, "จาก MMM / simulator": the channel's weekly budget at box 6's position, against its in-scope accounts'
 *   current weekly spend, the base of their shares (T10d) and so the head's 100% (T14d). A stopped channel reads
 *   หยุดวิ่ง by the bundle's own flag (T14c);
 * - then its accounts from allocationOf: live 28-day figures (plan R17), the Cost per Result with what it is per
 *   (D1's CostFigure, rules-04 E3), the efficiency index and the recommended split.
 * An estimate within the channel, not an MMM result, so nothing has a range. An account outside the scope is dimmed,
 * chipped นอก scope, and gets 0. A "—" index names which of its two causes it is (T14a). NULL prints "—", never 0.
 *
 * @module components/budget/AccountAllocation
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BoxMessage } from '@/components/looker/app/appBoxShared';
import { OverviewCard } from '@/components/overview/OverviewBox';
import { Chip, CostFigure, OverviewTable, type OverviewColumn } from '@/components/overview/OverviewTable';
import { PlatformDot } from '@/components/PlatformDot';
import type { AllocationRow } from '@/services/budget/accountAllocation';
import { MAX_SHIFT } from '@/services/budget/budgetContract';
import type { Allocation } from '@/services/budget/simulator';
import type { TFn } from '@/services/channels/channelFormat';
import { deltaOf, TONE_CLASS } from '@/services/looker/lookerDelta';
import { addNullable } from '@/services/overview/overviewMath';
import { DASH, fmtDelta, fmtMoney, fmtPct, objectiveText } from '@/services/overview/overviewFormat';
import { platformShortLabel } from '@/utils/platformLabel';

/** The prototype's arrows: an index above 1.05 is ▲, below 0.95 ▼. */
const ARROW = { up: 1.05, down: 0.95 } as const;
/** Dimmed as D1 fades a row; OverviewTable's own `faded` would title it a small sample. */
const DIM_CLASS = 'block italic text-neutral-700';

interface Head {
  channel: string;
  budget: number;
  /** The in-scope accounts' weekly spend; null when none reported any. */
  current: number | null;
  stopped: boolean;
}

/** A table line: a channel's head row or one of its accounts (OverviewTable has no row groups). */
type Line = { kind: 'head'; head: Head } | { kind: 'account'; row: AllocationRow };

/** Each channel of the budget that has an account, in the budget's order: its head row, then its accounts. */
const linesOf = (rows: readonly AllocationRow[], channelBudget: Allocation, stopped: readonly string[]): Line[] =>
  Object.entries(channelBudget).flatMap(([channel, budget]): Line[] => {
    const own = rows.filter((r) => r.channel === channel);
    if (own.length === 0) return [];
    // In scope only, where the prototype summed every account: the model and the simulator's budget cover only the
    // scope, so an all-account sum would put a Δ between an in-scope budget and all-account spend (T14d).
    const current = own.filter((r) => r.inScope).reduce<number | null>((s, r) => addNullable(s, r.weeklySpend), null);
    const head: Head = { channel, budget, current, stopped: stopped.includes(channel) };
    return [{ kind: 'head', head }, ...own.map((row): Line => ({ kind: 'account', row }))];
  });

const AccountName: React.FC<{ row: AllocationRow }> = ({ row }) => {
  const { t } = useTranslation();
  return <span className="pl-4">{row.accountName ?? row.accountId}{!row.inScope && <Chip text={t('budget.scope.outOfScope')} />}</span>;
};

const IndexFigure: React.FC<{ row: AllocationRow }> = ({ row }) => {
  const { t } = useTranslation();
  // Out of scope gets 0; in scope, there was no Result to compare in an objective it shares with the channel.
  if (row.index === null) return <span title={row.inScope ? t('budget.allocation.notComparable') : t('budget.allocation.outOfScope')}>{DASH}</span>;
  const up = row.index > ARROW.up;
  return (
    <>
      {row.index.toFixed(2)}
      {(up || row.index < ARROW.down) && <>{' '}<span data-index-arrow className={TONE_CLASS[up ? 'good' : 'bad']}>{up ? '▲' : '▼'}</span></>}
    </>
  );
};

/** 100% of a total that exists; with none, its accounts' shares are null too (T10d). */
const headShare = (h: Head): number | null => (h.current !== null && h.current > 0 ? 1 : null);

const bold = (on: boolean, node: React.ReactNode): React.ReactNode => (on ? <span className="font-semibold">{node}</span> : node);

/** The prototype's columns; the head row is bold, as its total row is, and leaves Cost per Result and the index blank. */
const columnsOf = (t: TFn, currency: string | null): Array<OverviewColumn<Line>> => [
  {
    id: 'account', header: t('budget.allocation.col.account'),
    cell: (l) => (l.kind === 'head'
      ? (
        <span className="inline-flex items-center gap-1.5">
          <PlatformDot platform={l.head.channel} />
          <span className="font-semibold">{platformShortLabel(l.head.channel)}</span>
          {l.head.stopped && <Chip text={t('budget.stopped')} />}
        </span>
      )
      : <AccountName row={l.row} />),
  },
  {
    id: 'objective', header: t('budget.allocation.col.objective'),
    cell: (l) => {
      if (l.kind === 'head') return <span className="text-neutral-700">{t('budget.allocation.fromSimulator')}</span>;
      return l.row.mainObjective === null ? DASH : objectiveText(l.row.mainObjective, t);
    },
  },
  {
    id: 'current', header: t('budget.allocation.col.current'), numeric: true,
    cell: (l) => bold(l.kind === 'head', fmtMoney(l.kind === 'head' ? l.head.current : l.row.weeklySpend, currency)),
  },
  {
    id: 'share', header: t('budget.allocation.col.share'), numeric: true,
    cell: (l) => bold(l.kind === 'head', fmtPct(l.kind === 'head' ? headShare(l.head) : l.row.share)),
  },
  {
    id: 'cpr', header: t('budget.allocation.col.cpr'), numeric: true,
    cell: (l) => (l.kind === 'head' ? null : <CostFigure row={{ metric: l.row.metric, costPerResult: l.row.cpr }} currency={currency} />),
  },
  { id: 'index', header: t('budget.allocation.col.index'), numeric: true, cell: (l) => (l.kind === 'head' ? null : <IndexFigure row={l.row} />) },
  {
    id: 'recommended', header: t('budget.allocation.col.recommended'), numeric: true,
    cell: (l) => bold(true, fmtMoney(l.kind === 'head' ? l.head.budget : l.row.recommended, currency)),
  },
  {
    id: 'delta', header: t('budget.allocation.col.delta'), numeric: true,
    // A budget move is neutral, as in the prototype; over no current spend there is no Δ.
    cell: (l) => bold(l.kind === 'head', fmtDelta(l.kind === 'head' ? deltaOf(l.head.budget, l.head.current) : deltaOf(l.row.recommended, l.row.weeklySpend))),
  },
];

/** Every cell of an out-of-scope account is dimmed, so the row still reads as one row of the table. */
const dimOutOfScope = (columns: Array<OverviewColumn<Line>>): Array<OverviewColumn<Line>> =>
  columns.map((c) => ({
    ...c,
    cell: (l: Line) => (l.kind === 'account' && !l.row.inScope ? <span data-out-of-scope className={DIM_CLASS}>{c.cell(l)}</span> : c.cell(l)),
  }));

interface Props {
  /** allocationOf(…), built by the page over the scope currency's rows only (spec §5.9). */
  rows: readonly AllocationRow[];
  /** The simulator's weekly budget per channel, at box 6's position (spec §5.7). */
  channelBudget: Allocation;
  /** The scope's one currency; null prints every money cell as "—". */
  currency: string | null;
  /** The bundle's stopped channels, by its own flag: never read off a budget of 0, which a running slider can reach. */
  stopped: readonly string[];
}

export const AccountAllocation: React.FC<Props> = ({ rows, channelBudget, currency, stopped }) => {
  const { t } = useTranslation();
  const lines = linesOf(rows, channelBudget, stopped);
  return (
    <OverviewCard id="budget-8" title={t('budget.allocation.title')}>
      <p data-allocation-label className="-mt-2 mb-3 text-xs text-neutral-700">
        {t('budget.allocation.label')} · {t('budget.allocation.window')}
      </p>
      {lines.length === 0
        ? <BoxMessage text={t('looker.tileEmpty')} />
        : (
          <OverviewTable
            columns={dimOutOfScope(columnsOf(t, currency))} rows={lines}
            rowKey={(l) => (l.kind === 'head' ? `head:${l.head.channel}` : `account:${l.row.accountId}`)}
          />
        )}
      <p data-allocation-note className="mt-2 text-xs text-neutral-700">
        {t('budget.allocation.note', {
          pct: fmtPct(MAX_SHIFT, 0), notComparable: t('budget.allocation.notComparable'), outOfScope: t('budget.allocation.outOfScope'),
        })}
      </p>
    </OverviewCard>
  );
};
